// Household setup (docs/FILE_SPLIT_RUN1_SPEC.md, run 1): WelcomeSetupCard, SetupWizard and
// WizardPreview stay here; the frame pieces, the steps, the add-a-child sheet and the all-done
// overlay now live under ./wizard/ and are re-exported unchanged below.

import { DailyHabitsSection } from "./AdjustSections.jsx";
import { CoinIcon, CopyrightFooter, Icon, SafeAreaSpacer, Toast, useToast } from "./Common.jsx";
import { LE_GUIDE_STEPS, LearningAdminTab } from "./LearningAdminTab.jsx";
import { ProfilesTab } from "./ProfilesTab.jsx";
import { newOwnerAdult } from "../lib/adults.js";
import { AVATARS, nowIso } from "../lib/constants.js";
import { defaultState } from "../lib/utils.js";
import { ChildContextBar } from "./ChildContextBar.jsx";
import { ChildTasksSection } from "./ChildTasksSection.jsx";
import { EarningsHealthLine } from "./EarningsHealthLine.jsx";
import { seedHouseholdFromCaps } from "../lib/spendingDefaults.js";
import { loadContentBundle, t as ct } from "../lib/content.js";
import { setMetricsScreen, track } from "../lib/metrics.js";
import { WIZARD_STEPS_AUTO, WIZARD_STEPS, STEP4_PARTS, WIZARD_STEP_KEY, WizardProgress, WizardIntro, WizardFooter, WizardBackButton } from "./wizard/wizardShared.jsx";
import { PinSetupStep, TokenSetupStep, SetupReviewStep, NotificationSetupStep } from "./wizard/steps.jsx";
import { SetupCompleteOverlay } from "./wizard/SetupComplete.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

export function WelcomeSetupCard() {
  return (
    <div className="px-0.5 py-2 text-center">
      <div className="text-xl font-extrabold text-slate-900 mb-2">{ct("wizard.gate.heading", "Welcome to your household")}</div>
      <p className="text-sm text-slate-500">{ct("wizard.gate.body", "A grown-up needs to set this up first — ask them to tap Parent Mode.")}</p>
    </div>
  );
}

export function SetupWizard({ state, update, updateAndSave, code, showToast, activeChild, setActiveChildId, addTransaction, setPendingVerifiedPin, setVerifiedPin, hiddenIds, onToggleVisibility, mutedIds, onToggleMute, onFinished }) {
  const step = state.onboardingStep || 1;
  // Step 4's own internal progressive-reveal sequence (Aug 2026 feedback): 1 = Daily habits
  // & streaks, 2 = Earning activities, 3 = Goals — see the section mounts below, which own
  // the reveal/expand directly (C1, Aug 2026: they used to be AdjustTab's own sections,
  // reordered via CSS `order` + controlled-open props keyed off a `wizardSubStep` prop
  // AdjustTab derived visibility from). Local (not persisted in `state`) since it's just a
  // within-step focus cursor, not a real setup decision.
  const [wizardSubStep, setWizardSubStep] = useState(1);
  /* Step 5's own cursor through LE_GUIDE_STEPS (Alan, 30 Aug 2026). The Learn & Earn step
   * used to hand a parent the whole Parent Mode dashboard — three collapsed accordions and
   * a tab bar — with no indication of what they were meant to do with it. It now walks the
   * six panes in order: spelling, vocabulary, English reward, times tables, maths workout,
   * maths reward. Local, like wizardSubStep, because it's a focus cursor rather than a
   * setup decision, and it restarts at 0 for every child.
   *
   * "Skip for now" still leaves the entire step in one tap — a parent who trusts the
   * defaults (which are genuinely fine: every game is on and already serving a list) should
   * not have to tap through six screens to say so. */
  const [lePart, setLePart] = useState(0);
  // 0..5 are the guided parts. The defaults gate that used to sit at -1 (30 Aug) is gone
  // (LB-127 PR3): the review screen on the auto route is where "these look good" lives
  // now, and a parent who chose "Take me through it" has asked for the parts, not for a
  // second chance to skip them.
  const leGuide = LE_GUIDE_STEPS[Math.min(Math.max(lePart, 0), LE_GUIDE_STEPS.length - 1)];
  const leIsLastPart = lePart >= LE_GUIDE_STEPS.length - 1;

  /* Route (LB-128). `onboardingRoute` is persisted so a reload mid-setup resumes on the
   * same path; a household that was mid-wizard when this shipped has no route and resumes
   * on the guided walk it was already on. */
  const route = state.onboardingRoute === "auto" ? "auto" : "custom";
  const steps = route === "auto" ? WIZARD_STEPS_AUTO : WIZARD_STEPS;
  /* Editing from the review (rider 1): a Change link opens the exact guided screen for
   * that part, and the part's Continue/Skip/Back all come back to the review rather than
   * marching on to the next child. Local, like the other cursors — it is where the parent
   * is, not what they decided. */
  const [reviewEditing, setReviewEditing] = useState(null); // { childIndex, target } | null
  const inReview = route === "auto" && step === 4 && !reviewEditing;
  function returnToReview() {
    setReviewEditing(null);
    setWizardSubStep(1);
    setLePart(0);
    setStep4Open({ habits: true, activities: false });
    goToStep(4);
  }
  function chooseRoute(nextRoute) {
    update((s) => {
      s.onboardingRoute = nextRoute;
      // BOTH routes seed here, from one function (rider 4). The fork changes navigation
      // only — a parent who backs out of the review into the guided walk finds the same
      // numbers, and the guided walk starts from the same seeded household the review shows.
      seedHouseholdFromCaps(s);
      return s;
    });
    track("setup_fork_choice", { kind: nextRoute, via: "wizard" });
    setChildIndex(0);
    setReviewEditing(null);
    setWizardSubStep(1);
    setLePart(0);
    setStep4Open({ habits: true, activities: false });
    goToStep(4);
  }
  function openFromReview(target) {
    setReviewEditing({ childIndex, target });
    if (target === "learnearn") {
      setLePart(0);
      goToStep(5);
      return;
    }
    setWizardSubStep(target === "tasks" ? 2 : 1);
    setStep4Open({ habits: target === "habits", activities: target === "tasks" });
  }
  function leaveReview() {
    if (nextChild) { setChildIndex((i) => i + 1); return; }
    setChildIndex(0);
    goToStep(afterChildSetup());
  }

  function leaveLearnEarnStep() {
    if (reviewEditing) { returnToReview(); return; }
    // Step 5 walks children in sequence too (Alan, 30 Aug): Learn & Earn carries most of a
    // child's cap, and its reward values differ per child whenever caps do, so a blended
    // single pass would hide exactly the numbers this step exists to set.
    // The only place the child cursor moves. Loops back to step 4 so the next child gets
    // their own end-to-end pass, rather than the household being walked step by step.
    // The skip-all interstitial that used to fire here is gone (LB-132 seeds a standard set
    // of habits at the fork, so its "zero habits" condition could never hold again).
    if (nextChild) {
      setChildIndex((i) => i + 1);
      setWizardSubStep(1);
      setLePart(0);
      setStep4Open({ habits: true, activities: false });
      goToStep(4);
      return;
    }
    setChildIndex(0);
    goToStep(afterChildSetup());
  }
  // Per-section open state for step 4 (Aug 2026, A4): advancing wizardSubStep opens the
  // newly revealed section and collapses the previous one to its header line
  // (CollapsibleSection's compactClosed), but each section stays independently reopenable —
  // tapping a collapsed header's title flips its own entry here, not tied to wizardSubStep.
  const [step4Open, setStep4Open] = useState({ habits: true, activities: false });

  /* ---------------------- sequential per-child setup (steps 4 & 5) ----------------------
   * Steps 4 and 5 used to show ChildChipRow — a row of child chips the parent tapped to
   * switch between. Removed (Alan, 30 Aug 2026) for two reasons: it scrolls out of view
   * exactly when the "whose settings are these?" question matters, and it lets a parent
   * finish setup having silently configured only one of their children.
   *
   * Sequential instead: the wizard walks children in order, the CTA names the next one, and
   * ChildContextBar states who is on screen without ever being scrolled away. There is no
   * wrong child to be on, so there is nothing to switch.
   */
  const [childIndex, setChildIndex] = useState(0);
  // LB-19 Zip B (screen 6, signed off): the L&E step leads with the Adaptive explainer —
  // Continue accepts it (Adaptive is on by default, starter lists auto-serve, so there is
  // nothing to configure) and moves on; "I want to do it myself" switches the rest of the
  // wizard to the guided per-child parts. Review edits always open the manual parts.
  const [leManual, setLeManual] = useState(false);
  const orderedChildren = state.profiles || [];
  const stepChild = orderedChildren[Math.min(childIndex, Math.max(0, orderedChildren.length - 1))] || activeChild;
  const nextChild = orderedChildren[childIndex + 1] || null;

  // Keep App's own active child in step with the wizard's cursor, so anything reading
  // activeChild (the header, a deep link, the preview harness) agrees with what's on screen.
  useEffect(() => {
    if (stepChild && setActiveChildId && stepChild.id !== activeChild?.id && (step === 4 || step === 5)) {
      setActiveChildId(stepChild.id);
    }
  }, [stepChild?.id, step]);

  /* Step-4 entry seeding (LB-127 PR3). The fork seeds every child (chooseRoute above);
   * this only catches a child added by walking back to step 1 after the fork, or a cap
   * changed by walking back to step 3. seedHouseholdFromCaps is idempotent — a child whose
   * cap has not moved is left exactly as they are, ids and parent edits included — so
   * running it on every step-4 landing is safe. `return null` keeps a no-op from bumping
   * the revision. The per-child "inherit child 1" pre-staging that lived here is
   * superseded: every child now gets their own standard set from their own cap, and
   * same-band siblings are linked by seedHouseholdFromCaps (LB-133). */
  useEffect(() => {
    if (step !== 4 || !stepChild) return;
    update((s) => (seedHouseholdFromCaps(s) ? s : null));
  }, [step, stepChild?.id]);

  // Advance to the next child, or off the end of the step. Sub-step reveal restarts for each
  // child: part 1 of 3 is where the next child's setup begins, not wherever the last one left off.
  /* Steps 4 and 5 are ONE pass per child, not two passes over the household (Alan, 30 Aug
   * 2026). The first cut advanced the child within each step — every child through Earning,
   * then every child through Learn & Earn — so a parent set up Alex, was moved to Maya, and
   * then found themselves back on Alex two screens later. Setting up a child is one job;
   * being bounced between two of them mid-job makes it feel like the wizard has lost its
   * place. Now: Alex's earning, Alex's Learn & Earn, then Maya's earning, Maya's Learn &
   * Earn, then Notifications.
   *
   * So step 4 never advances the child at all — it always hands to step 5 for the SAME
   * child — and step 5 is the only place the child index moves, looping back to step 4 for
   * the next one. */
  function leaveEarningStep() {
    if (reviewEditing) { returnToReview(); return; }
    setWizardSubStep(1);
    setLePart(0);
    goToStep(5);
  }
  // Two parts, not three (Alan, 30 Aug): Goals left the wizard. Step 1 already tells
  // parents they can leave goals to the kids, goals are the child's own aspiration rather
  // than a setup chore, and this step now walks EVERY child in turn — so the part most
  // parents skipped was also the one multiplying fastest. Goals remain fully available in
  // Parent Mode (Kids ▸ child ▸ Goals) and to the kids themselves.
  function advanceWizardSubStep() {
    const next = Math.min(wizardSubStep + 1, STEP4_PARTS);
    setWizardSubStep(next);
    setStep4Open({ habits: next === 1, activities: next === 2 });
  }
  function goToStep(n) { update((s) => { s.onboardingStep = n; return s; }); }
  /* Every wizard screen opens at the top (Alan, 30 Aug 2026). The Continue buttons sit at
   * the FOOT of a long step, so advancing left the page scrolled exactly where the tap
   * happened — a parent arrived on the next screen already past its progress bar, its
   * heading and its intro copy, with no clue how far through they were or what the screen
   * was for. The same applies within a step: step 4's parts and step 5's six Learn & Earn
   * parts each swap the content out beneath a scrolled viewport.
   *
   * Instant, not smooth: this is a page change, not a nudge within one, and animating it
   * would show the parent a scroll they didn't ask for. Guarded because jsdom (the pre-ship
   * mount harness) has no scrollTo.
   */
  // Alan, 4 Sep: a part change (habits → tasks, L&E part 2 → 3) used to jump to the page
  // top, past the progress card, so the parent lost their place. Within steps 4 and 5 the
  // child bar is the anchor: a sub-step change scrolls it to the top of the viewport; a
  // STEP or CHILD change is a page change and still goes to the top.
  const anchorRef = useRef(null);
  const lastPos = useRef({ step, childIndex });
  useEffect(() => {
    const sameChild = lastPos.current.step === step && lastPos.current.childIndex === childIndex;
    lastPos.current = { step, childIndex };
    try {
      if (sameChild && (step === 4 || step === 5) && anchorRef.current && anchorRef.current.scrollIntoView) anchorRef.current.scrollIntoView({ block: "start", behavior: "auto" });
      else window.scrollTo({ top: 0, behavior: "auto" });
    } catch {}
  }, [step, wizardSubStep, lePart, childIndex]);
  function finish() {
    update((s) => { s.onboardingDone = true; return s; });
    track("wizard_completed", {});
    if (onFinished) onFinished();
  }
  // W2.5 wizard funnel: one wizard_step_reached per step LANDING (forward or back — the
  // dashboard's per-step drop-off view dedupes by device, so re-visits don't inflate it).
  // Fires for the preview wizard too, but the worker drops code "PREVIEW" batches, so
  // demos and the Playwright suite never pollute the funnel.
  // `kind` carries the sub-step cursor (analytics audit LB-60 §4.1): steps 4 and 5 walk
  // children in sequence with parts each, and without it the dataset could not say whether
  // a parent gave up on child 1 part 2 or child 2 part 6. Same event name and `step` shape,
  // so before-and-after the LB-127 rewrite stays comparable; `kind` is empty on steps
  // that have no sub-step, exactly as before. Fires on every sub-step LANDING; the
  // dashboard's per-step drop-off view dedupes by device so this does not inflate it.
  const subStepKind = step === 4
    ? (inReview ? `${route}:c${childIndex + 1}:review` : `${route}:c${childIndex + 1}:${wizardSubStep === 1 ? "habits" : "tasks"}`)
    : step === 5 ? `${route}:c${childIndex + 1}:le${lePart + 1}` : "";
  // The funnel keys on the step's NAME from 20 Sep (see WIZARD_STEP_KEY); rows written
  // before then carry the number, and the admin dashboard maps those to the same names.
  const stepKey = WIZARD_STEP_KEY[step] || String(step);
  useEffect(() => {
    // The screen series SPLITS at this release: rows written before it read
    // "wizard:<number>", rows after it read "wizard:<name>". Nothing reads the screen field
    // today, which is why this is a note rather than a mapping — but anything that starts
    // reporting on it has to put the old numbers through WIZARD_STEP_NUMBER_TO_KEY in
    // worker/handlers/analytics.js first, the same table the funnel uses, or the same step
    // shows up as two series either side of 20 Sep.
    setMetricsScreen("wizard:" + stepKey);
    track("wizard_step_reached", subStepKind ? { step: stepKey, kind: subStepKind } : { step: stepKey });
  }, [stepKey, subStepKind]);
  // A household that already had a real PIN set before the wizard ever ran it (e.g. an
  // existing household that just never got round to adding children) shouldn't be walked
  // through PinSetupStep — it assumes the household is still on the "123456" placeholder,
  // which would fail the server's proof-of-old-PIN check for anyone who'd already changed it.
  // PIN before Notifications (Alan, 20 Sep): Children always goes on to Token Setup, and the
  // PIN comes once every child is set up. A household that already has a real PIN (an
  // existing household that never got round to adding children) goes straight to
  // Notifications, for the reason the old afterChildren skipped it: PinSetupStep assumes the
  // "123456" placeholder, which would fail the server's proof-of-old-PIN for anyone who has
  // already changed it.
  function afterChildren() { goToStep(3); }
  function afterChildSetup() { return state.settings.pinConfigured ? 6 : 2; }

  // Back navigation (Alan, Aug 2026).
  //
  // Step 2 (PinSetupStep) is SKIPPED on the way back whenever a PIN is already configured,
  // for exactly the reason afterChildren skips it on the way forward: PinSetupStep.save()
  // hardcodes setPendingVerifiedPin("123456"), proving knowledge of the STARTER pin. That's
  // correct on first run, but a parent who has since set a real PIN would send a proof the
  // server rejects. Re-entering that step is therefore never safe once pinConfigured is
  // true — changing a PIN already has a home in Settings.
  //
  // Returns null for a step with nowhere to go back to, which is what hides the button.
  function backTargetFor(n) {
    if (n <= 1) return null;
    if (n === 3) return 1;
    const lastChildStep = route === "auto" ? 4 : 5;
    if (n === 2) return lastChildStep;
    if (n === 6) return state.settings.pinConfigured ? lastChildStep : 2;
    return n - 1;
  }
  const backTarget = backTargetFor(step);
  // Going back from the PIN (or from Notifications) re-enters the per-child steps, so it
  // lands on the LAST child's last screen — where the parent just was — not child 1 part 1.
  function backIntoChildren(target) {
    setReviewEditing(null);
    setChildIndex(Math.max(0, orderedChildren.length - 1));
    setWizardSubStep(1);
    setLePart(leManual ? LE_GUIDE_STEPS.length - 1 : 0);
    goToStep(target);
  }
  const goBack = reviewEditing
    ? returnToReview
    : backTarget
      ? ((step === 2 || step === 6) && (backTarget === 4 || backTarget === 5) ? () => backIntoChildren(backTarget) : () => goToStep(backTarget))
      : null;
  // Back on the review walks the children before it walks the fork.
  const reviewBack = () => { if (childIndex > 0) setChildIndex((i) => i - 1); else goToStep(3); };

  return (
    <div className="pop lb-form wizard-grid">
      <WizardProgress
        step={step}
        steps={steps}
        subStep={inReview ? null : step === 4 ? wizardSubStep : step === 5 ? lePart + 1 : null}
        subSteps={step === 5 ? LE_GUIDE_STEPS.length : STEP4_PARTS}
      />

      {step === 1 && (
        <>
          <WizardIntro icon={<Icon id="ic-children" size={30} />} title={ct("wizard.children.title", "Add children")} body={ct("wizard.children.body", "Create a profile for each child in your household.")} />
          <ProfilesTab state={state} update={update} updateAndSave={updateAndSave} code={code} showToast={showToast} setActiveChildId={setActiveChildId} addTransaction={addTransaction} hiddenIds={hiddenIds} onToggleVisibility={onToggleVisibility} mutedIds={mutedIds} onToggleMute={onToggleMute} hideStartingBalance autoExpandNewChild={false} />
          <WizardFooter>
            <button disabled={state.profiles.length === 0} className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 disabled:opacity-40 text-white font-bold text-sm rounded-lg py-2.5" onClick={afterChildren}>
              {state.profiles.length === 0 ? ct("wizard.children.need_one", "Add at least one child to continue") : ct("wizard.common.continue", "Continue")}
            </button>
          </WizardFooter>
        </>
      )}

      {step === 2 && <PinSetupStep state={state} update={update} setPendingVerifiedPin={setPendingVerifiedPin} setVerifiedPin={setVerifiedPin} showToast={showToast} onBack={goBack} />}

      {step === 3 && <TokenSetupStep state={state} update={update} showToast={showToast} goToStep={goToStep} onBack={goBack} onChooseRoute={chooseRoute} />}

      {inReview && stepChild && (
        <SetupReviewStep
          child={stepChild}
          state={state}
          update={update}
          showToast={showToast}
          index={childIndex}
          total={orderedChildren.length}
          nextChild={nextChild}
          onChange={openFromReview}
          onContinue={leaveReview}
          onBack={reviewBack}
        />
      )}

      {step === 4 && !inReview && (
        <>
          <WizardIntro header={<div ref={anchorRef} style={{ scrollMarginTop: "8px" }}> <ChildContextBar child={stepChild} contextLine={ct("wizard.earn.child_context", "Setting up {name}'s earning", { name: stepChild?.name || "" })} index={childIndex} total={orderedChildren.length} />
</div>} 
            icon={<CoinIcon className="w-9 h-9" />}
            title={ct("wizard.habits.title", "Daily habits and earning activities")}
            body={
              wizardSubStep === 1
                ? ct("wizard.habits.body1", "Start with the habit you want front and centre every day — how many tasks earn the weekly streak reward, and how much it pays. You can always add more later.")
                : ct("wizard.habits.body2", "Now the tasks they can do any time to earn tokens — reading, tidying, homework, whatever fits your household. We've priced these to fit their weekly cap; change anything you like.")
            }
          />
          {stepChild ? (
            <div className="flex flex-col gap-2">
              <DailyHabitsSection child={stepChild} state={state} update={update} setupMode open={step4Open.habits} onToggle={(v) => setStep4Open((m) => ({ ...m, habits: v }))} compactClosed />
              {/* The wizard mounts ChildTasksSection rather than AdjustSections' shipped
                  EarningActivitiesSection: setup needs "pick a couple of things fast at
                  sensible prices", parent mode needs award-on-tap, photo proof and editing.
                  Same `activities` rows either way — different job, different surface. */}
              {wizardSubStep >= 2 && (
                <ChildTasksSection child={stepChild} state={state} update={update} />
              )}
              <EarningsHealthLine child={stepChild} state={state} update={update} showToast={showToast} />
            </div>
          ) : (
            <div className="px-0.5 py-4 text-center text-base text-slate-400">{ct("wizard.habits.no_children", "Add a child first to set up their earning.")}</div>
          )}
          <WizardFooter>
            <WizardBackButton onBack={goBack} />
            {!reviewEditing && (
              <button className="flex-1 md:flex-none md:px-4 bg-pill text-navy font-bold text-sm rounded-lg py-2.5" onClick={() => (wizardSubStep < STEP4_PARTS ? advanceWizardSubStep() : leaveEarningStep())}>{ct("wizard.common.skip_for_now", "Skip for now")}</button>
            )}
            <button className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5" data-testid="wizard-step4-continue" onClick={() => (reviewEditing ? returnToReview() : wizardSubStep < STEP4_PARTS ? advanceWizardSubStep() : leaveEarningStep())}>
              {reviewEditing
                ? ct("wizard.review.back_to_review", "Back to {name}'s setup", { name: stepChild?.name || "" })
                : wizardSubStep < STEP4_PARTS
                  ? ct("wizard.common.continue", "Continue")
                  : ct("wizard.earn.next_learnearn", "Next: {name}'s Learn & Earn", { name: stepChild?.name || "" })}
            </button>
          </WizardFooter>
          {/* Journey review: the granular per-part Skip stays; this is the one-tap escape
              for the "set up basics now, configure tonight" parent. Part 1 only — by part
              2 the remaining saving is a single tap. Not while editing from the review —
              there is nothing to skip to but the review itself. */}
          {wizardSubStep === 1 && !reviewEditing && (
            <p className="text-center md:text-right -mt-1 wizard-after-footer">
              <button className="text-xs font-bold text-slate-400 underline underline-offset-2" data-testid="skip-rest-of-step" onClick={() => goToStep(5)}>
                {ct("wizard.habits.skip_rest", "Skip the rest of this step")}
              </button>
            </p>
          )}
        </>
      )}

      {step === 5 && !leManual && !reviewEditing && (
        <>
          <div ref={anchorRef} style={{ scrollMarginTop: "8px" }} />
          <div className="rounded-xl px-4 py-4 text-white" style={{ background: "#1B2A4A" }} data-testid="wizard-le-advert">
            <div className="flex items-center gap-2 text-sm font-extrabold flex-wrap">{ct("adaptive.explainer.title", "Learning Coach")}<span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">{ct("adaptive.hub.free_trial", "Free in the Preview")}</span></div>
            <p className="text-xs text-white/90 mt-2">{ct("adaptive.explainer.p1", "Spelling, vocabulary, maths and times tables your kids play to earn tokens.")}</p>
            <p className="text-xs text-white/90 mt-1.5">{ct("adaptive.explainer.p2", "Learning Coach sets the level automatically, starting from their school year, so they are always challenged and progressing.")}</p>
            <p className="text-xs text-white/90 mt-1.5">{ct("adaptive.explainer.p3", "Questions adjust as they play, tricky topics come back until they stick, and you get a summary every week.")}</p>
          </div>
          <WizardFooter>
            <WizardBackButton onBack={goBack} />
            <button className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5" data-testid="wizard-le-advert-continue" onClick={leaveLearnEarnStep}>{ct("wizard.common.continue", "Continue")}</button>
          </WizardFooter>
          <button className="w-full text-center text-sm font-bold text-slate-900 py-2" data-testid="wizard-le-manual-link" onClick={() => setLeManual(true)}>{ct("wizard.learnearn.do_it_myself", "I want to do it myself")}</button>
        </>
      )}

      {step === 5 && (leManual || reviewEditing) && (
        <>
          {/* The intro belongs on the gate only. Inside the guided parts the step header
              names the part and says what it is for, so a second "this is already set up"
              card above it is a screenful of reassurance a parent has already accepted by
              choosing to look. */}
          <WizardIntro
            header={<div ref={anchorRef} style={{ scrollMarginTop: "8px" }}> <ChildContextBar child={stepChild} contextLine={ct("wizard.learnearn.child_context", "Setting up {name}'s Learn & Earn", { name: stepChild?.name || "" })} index={childIndex} total={orderedChildren.length} />
</div>}
            eyebrow={<span data-testid="lea-guide-header">{ct("learnearn.guide.position", "Part {n} of {total} · {label}", { n: lePart + 1, total: LE_GUIDE_STEPS.length, label: leGuide.label })}</span>}
            icon={<Icon id="ic-brain" size={30} />}
            title={ct(`learnearn.guide.heading_${leGuide.id}`, leGuide.heading)}
            body={ct(`learnearn.guide.todo_${leGuide.id}`, leGuide.todo)}
          />
          {!stepChild ? (
            <div className="px-0.5 py-4 text-center text-base text-slate-400">{ct("wizard.learnearn.no_children", "Add a child first to set up Learn & Earn.")}</div>
          ) : (
            <>
              <LearningAdminTab state={state} update={update} activeChild={stepChild} showToast={showToast} guideStep={leGuide.id} guideHeader={false} />
              {/* Learn & Earn carries 60% of a child's cap under the default split, so this
                  is the step where the health line is most likely to have something to say. */}
              <EarningsHealthLine child={stepChild} state={state} update={update} showToast={showToast} />
              <WizardFooter>
                {/* Back walks the parts before it walks the gate — inside a six-part
                    sequence, a Back that jumped all the way out to step 4 would be a trap. */}
                <WizardBackButton onBack={lePart > 0 ? () => setLePart((p) => p - 1) : goBack} />
                <button className="flex-1 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl py-2.5" data-testid="wizard-step5-skip" onClick={leaveLearnEarnStep}>
                  {reviewEditing ? ct("wizard.review.back_to_review_short", "Back to setup") : ct("wizard.common.skip_for_now", "Skip for now")}
                </button>
                <button className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5" data-testid="wizard-step5-continue" onClick={() => (leIsLastPart ? leaveLearnEarnStep() : setLePart((p) => p + 1))}>
                  {!leIsLastPart
                    ? ct("wizard.common.continue", "Continue")
                    : reviewEditing ? ct("wizard.review.back_to_review", "Back to {name}'s setup", { name: stepChild?.name || "" })
                      : nextChild ? ct("wizard.earn.next_child", "Next: {name}", { name: nextChild.name }) : ct("wizard.common.continue", "Continue")}
                </button>
              </WizardFooter>
            </>
          )}
        </>
      )}

      {step === 6 && <NotificationSetupStep code={code} onFinish={finish} onBack={goBack} />}

    </div>
  );
}

/* ------------------------------ legal pages -------------------------------- */

// Shared shell for the Terms/Privacy pages — reached via littlebankers.uk/terms and /privacy
// (LB-8; the old ?page= forms are 301s), and linked from the household-creation checkbox and
// the Settings tab.

export function WizardPreview() {
  const [state, setState] = useState(() => {
    const s = defaultState();
    // spendingCap seeded explicitly (same house rule as buildPreviewState — preview state
    // never passes through migrateState): without it spendingCapPence() reads "no cap in
    // force", and the whole guardrail surface — suggested values, the health line, Reprice —
    // correctly renders nothing, which would make this preview a misleading demo of the
    // step it exists to preview.
    // TWO children, deliberately (staging bug 14, 30 Aug 2026). Steps 4 and 5 walk children
    // in sequence now, and this preview seeded exactly one — so the entire per-child
    // sequence (the "Next: {name}" CTA, the "Child 1 of 2" counter, the pre-staging of the
    // first child's tasks onto the second) had no preview and no spec coverage, and shipped
    // broken. Different caps on purpose: it is the case where copied tasks must be re-priced
    // rather than duplicated.
    s.profiles.push({ id: "preview-child-1", name: "Alex", avatar: AVATARS[0], age: 10, tokenBalance: 12, vaultBalancePence: 0, earnsVaultInterest: true, goals: [], spendingCap: { enabled: true, weeklyGBP: 5 }, habitReward: 20, habitThreshold: 5 });
    s.profiles.push({ id: "preview-child-2", name: "Maya", avatar: AVATARS[1], age: 6, tokenBalance: 0, vaultBalancePence: 0, earnsVaultInterest: true, goals: [], spendingCap: { enabled: true, weeklyGBP: 3 }, habitReward: 15, habitThreshold: 4 });
    s.learningContent["preview-child-1"] = { spellingWords: [], vocabBank: [] };
    s.learningContent["preview-child-2"] = { spellingWords: [], vocabBank: [] };
    // LB-339/LB-340: a real household always has its Owner row by the time the wizard runs —
    // Root.jsx's handleStart seeds it at creation and the worker's migration supplies it on
    // any read. defaultState() alone does not (it cannot invent a pin), and this preview is
    // the one mount that skips both. Seeded here for the same reason buildPreviewState seeds
    // every persisted field: without it the PIN step has no adult to write to, which is a
    // shape no real session is ever in.
    s.adults = [newOwnerAdult({ name: s.ownerName, email: s.ownerEmail })];
    s.settings.ownerAdultId = s.adults[0].id;
    return s;
  });
  const [activeChildId, setActiveChildId] = useState("preview-child-1");
  const [toast, showToast] = useToast();
  // Preview-only local flag — mirrors App.jsx's showSetupComplete, just without the
  // hasSeenSetupComplete() persistence check, so the overlay is always reachable here
  // regardless of what a previous real session may have dismissed.
  const [showSetupComplete, setShowSetupComplete] = useState(false);
  const activeChild = state.profiles.find((p) => p.id === activeChildId) || state.profiles[0];

  // W1.10 (Aug 2026): ?preview=wizard renders OUTSIDE <Root /> (see main.jsx), so Root's
  // own content-bundle fetch never runs here — the preview was showing whatever bundle a
  // previous real session happened to leave in localStorage (or the baked fallbacks on a
  // fresh device), silently ignoring anything newly published in the CMS. Fetch it here
  // too, and — unlike Root, where next-load pickup is fine — force one re-render when the
  // bundle lands, since reviewing freshly published copy is this page's whole purpose.
  const [, setContentLoadedTick] = useState(0);
  useEffect(() => {
    let live = true;
    loadContentBundle().then(() => { if (live) setContentLoadedTick((n) => n + 1); });
    return () => { live = false; };
  }, []);

  function update(fn) {
    setState((prev) => {
      const next = fn(JSON.parse(JSON.stringify(prev)));
      // `return null` = no-op is the app-wide update() convention of record (App.jsx). The
      // preview lacked it, so any component using it — the step-4 seeding pass, the review's
      // cap commit — crashed the demo while working in the real app (found by the jsdom
      // route walk, 2 Sep).
      if (next === null || next === undefined) return prev;
      next._meta = { revision: ((prev._meta && prev._meta.revision) || 0) + 1, lastModified: nowIso() };
      return next;
    });
  }

  return (
    <div className="max-w-lg mx-auto p-3 sm:p-5 lb-frame">
      <SafeAreaSpacer />
      <Toast toast={toast} />
      <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold rounded-2xl px-3 py-2 text-center">
        Preview mode — nothing on this page is saved. Remove <code>?preview=wizard</code> from the URL to exit.
      </div>
      {/* V.3: the preview no longer shows a child strip — the real wizard does not either. */}
      <SetupWizard
        state={state}
        update={update}
        updateAndSave={async (fn) => { update(fn); return true; }}
        code="PREVIEW"
        showToast={showToast}
        activeChild={activeChild}
        setActiveChildId={setActiveChildId}
        addTransaction={() => false}
        setPendingVerifiedPin={() => {}}
        setVerifiedPin={() => {}}
        hiddenIds={[]}
        onToggleVisibility={() => {}}
        mutedIds={[]}
        onToggleMute={() => {}}
        onFinished={() => setShowSetupComplete(true)}
      />
      {/* Preview mount: `state` and `update` are the throwaway local pair this preview
          already uses. `preview` makes Show QR say the QR needs a real household rather
          than surfacing a PIN error from a household that doesn't exist (V.3).
          firstChild drives the Kid Mode primary. */}
      {showSetupComplete && (
        <SetupCompleteOverlay
          code={"PREVUE"}
          state={state}
          update={setState ? (fn) => setState((prev) => { const next = JSON.parse(JSON.stringify(prev)); fn(next); return next; }) : undefined}
          verifiedPin={null}
          firstChild={state.profiles?.[0] || null}
          onClose={() => setShowSetupComplete(false)}
          onShowKid={() => setShowSetupComplete(false)}
        preview />
      )}
      <CopyrightFooter />
    </div>
  );
}

export { STEP4_PARTS, WizardProgress, CollapsibleInfoRow, WizardIntro, WizardFooter, WizardBackButton, WIZARD_STEP_KEY, WIZARD_STEPS, WIZARD_STEPS_AUTO } from "./wizard/wizardShared.jsx";
export { PinSetupStep, SpendingCapStep, TokenSetupStep, NotificationSetupStep, CUSTOM_MINUTES_PER_CHILD, SetupFork, SetupReviewStep } from "./wizard/steps.jsx";
export { NewChildSetupSheet } from "./wizard/NewChildSetupSheet.jsx";
export { SETUP_COMPLETE_SEEN_KEY, hasSeenSetupComplete, SetupCompleteOverlay } from "./wizard/SetupComplete.jsx";
